import { useEffect, useState, ComponentProps, useMemo } from 'react';
import moment from 'moment';
import { useRouter } from 'next/router';
import GuestCounter from '../../common/GuestCounter/GuestCounter';
import DatePicker from '../../common/DatePicker/DatePicker';
import useAppDispatch from '../../../core/redux/useAppDispatch';
import {
  COMMON_DATE_FORMAT,
  DEFAULT_CHECK_IN_DATE,
  DEFAULT_CHECK_IN_DATE_MOMENT,
  DEFAULT_CHECK_OUT_DATE,
  DEFAULT_GUEST_COUNT,
  MiniSiteTabOptions,
} from '../../../core/constants';
import getGuestCount from '../../../core/booking/getGuestCount';
import bookingSlice from '../../../core/booking/slice';
import { useSelector } from '../../../core/redux/store';
import useGetHotelById from '../../../core/hotel/useGetHotelById';

type RoomAndGuestCount = ComponentProps<typeof GuestCounter>['roomAndGuestCount'];
type CheckInAndOutDates = ComponentProps<typeof DatePicker>['checkInAndOutDates'];

const useAvailability = (hotelId: string, isTLLMaintenance: boolean, locale?: string) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const {
    checkInDateISOString,
    checkOutDateISOString,
    roomCount,
    guestCountString: storedGuestCountString,
  } = useSelector((state) => state.booking);
  const getStoredGuestCount = getGuestCount(
    (router?.query?.guestPerRoom as string) || storedGuestCountString || DEFAULT_GUEST_COUNT
  );
  const storage = typeof window !== 'undefined' ? window?.sessionStorage : undefined;
  const fromConfig = !!storage?.getItem('prevPath')?.includes('config');
  const [roomAndGuestCount, setRoomAndGuestCount] = useState<RoomAndGuestCount>({
    roomCount: Number(router?.query?.roomCount) || roomCount || 1,
    adultCount: getStoredGuestCount.adultCount,
    seniorElementarySchoolChildCount: getStoredGuestCount.seniorElementarySchoolChildCount,
    juniorElementarySchoolChildCount: getStoredGuestCount.juniorElementarySchoolChildCount,
    infantWithMealAndFutonCount: getStoredGuestCount.infantWithMealAndFutonCount,
    infantWithMealCount: getStoredGuestCount.infantWithMealCount,
    infantWithFutonCount: getStoredGuestCount.infantWithFutonCount,
    infantWithoutMealAndFutonCount: getStoredGuestCount.infantWithoutMealAndFutonCount,
  });

  const calculatedCheckInDate = useMemo(
    () =>
      ((fromConfig && checkInDateISOString) || router.query.checkInDate) &&
      moment(router.query.checkInDate || checkInDateISOString).isSameOrAfter(moment(), 'days')
        ? moment(router.query.checkInDate || checkInDateISOString)
        : DEFAULT_CHECK_IN_DATE_MOMENT,
    [fromConfig, checkInDateISOString, router.query.checkInDate]
  );
  const calculatedCheckOutDate = useMemo(
    () =>
      ((fromConfig && checkOutDateISOString) || router.query.checkOutDate) &&
      moment(router.query.checkOutDate || checkOutDateISOString).isAfter(
        calculatedCheckInDate,
        'days'
      )
        ? moment(router.query.checkOutDate || checkOutDateISOString)
        : moment(calculatedCheckInDate).add(1, 'days'),
    [fromConfig, checkOutDateISOString, router.query.checkOutDate, calculatedCheckInDate]
  );
  const [checkInAndOutDates, setCheckInAndOutDates] = useState<CheckInAndOutDates>({
    checkInDate: calculatedCheckInDate,
    checkOutDate: calculatedCheckOutDate,
  });

  const guestCountString = `${roomAndGuestCount.adultCount}_${roomAndGuestCount.seniorElementarySchoolChildCount}_${roomAndGuestCount.juniorElementarySchoolChildCount}_${roomAndGuestCount.infantWithMealAndFutonCount}_${roomAndGuestCount.infantWithMealCount}_${roomAndGuestCount.infantWithFutonCount}_${roomAndGuestCount.infantWithoutMealAndFutonCount}`;
  const getLinkUrl = (roomId: number, planId: string) => {
    const queryString = [
      `room-id=${roomId}`,
      `plan-id=${planId}`,
      `checkin-date=${checkInAndOutDates.checkInDate?.format(COMMON_DATE_FORMAT)}`,
      `checkout-date=${checkInAndOutDates.checkOutDate?.format(COMMON_DATE_FORMAT)}`,
      `room-count=${roomAndGuestCount.roomCount}`,
      `guest-count=${guestCountString}`,
    ];

    return `/b/${hotelId}/configure?${queryString.join('&')}`;
  };
  const checkInDate = checkInAndOutDates.checkInDate?.format(COMMON_DATE_FORMAT);
  const checkOutDate = checkInAndOutDates.checkOutDate?.format(COMMON_DATE_FORMAT);

  const updateUrl = () => {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          checkInDate,
          checkOutDate,
          guestPerRoom: guestCountString,
          roomCount: roomAndGuestCount.roomCount.toString(),
        },
      },
      undefined,
      { shallow: true, scroll: false }
    );
  };
  // call on mounted to get updated availabilities
  useEffect(() => {
    // if the page is too old withtout being refreshed and checkInDate is before today's date
    // reflesh the page with default checkIn/Out dates without hitting the API
    if (!moment(checkInDate).isSameOrAfter(moment(), 'days')) {
      router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          checkInDate: DEFAULT_CHECK_IN_DATE,
          checkOutDate: DEFAULT_CHECK_OUT_DATE,
          tab: MiniSiteTabOptions.HOTEL_DETAILS,
        },
      });

      return undefined;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: hotel,
    isLoading: getByIdLoading,
    isError: getByIdFailed,
  } = useGetHotelById({
    hotelId,
    checkInDate: (router.query.checkInDate || DEFAULT_CHECK_IN_DATE) as string,
    checkOutDate: (router.query.checkOutDate || DEFAULT_CHECK_OUT_DATE) as string,
    guestCountString: (router?.query?.guestPerRoom || DEFAULT_GUEST_COUNT) as string,
    roomCount: Number(router?.query?.roomCount) || 1,
    locale: locale as string,
    routerIsReady: router.isReady,
  });

  useEffect(() => {
    dispatch(
      bookingSlice.actions.updateDateAndGuestCount({
        guestCountString,
        checkInDateISOString: checkInAndOutDates?.checkInDate?.toISOString() || '',
        checkOutDateISOString: checkInAndOutDates?.checkOutDate?.toISOString() || '',
        roomCount: roomAndGuestCount.roomCount,
      })
    );
  }, [
    checkInAndOutDates?.checkInDate,
    checkInAndOutDates?.checkOutDate,
    dispatch,
    guestCountString,
    roomAndGuestCount.roomCount,
  ]);

  return {
    dispatch,
    hotel,
    getByIdLoading,
    getByIdFailed,
    roomAndGuestCount,
    setRoomAndGuestCount,
    checkInAndOutDates,
    setCheckInAndOutDates,
    getLinkUrl,
    guestCountString,
    updateUrl,
  };
};

export default useAvailability;
